/**
 * CoherencePanel — GCE Dashboard Tab (Tier 2)
 * 
 * Shows alignment score arc, tier badge, breakdown, and autonomy limits.
 * Integrates into CognitiveLab as a tab.
 */
import React from 'react';

interface AlignmentData {
  score: number;
  tier: string;
  breakdown?: {
    factNodes?: number;
    preferenceNodes?: number;
    boundaryNodes?: number;
    behaviorNodes?: number;
    evidenceNodes?: number;
    totalTraining?: number;
    correctionRate?: number;
  };
}

interface AutonomyLimits {
  canRespondWithoutReview?: boolean;
  canExecuteWorkflows?: boolean;
  canSendExternalMessages?: boolean;
  canMakeFinancialDecisions?: boolean;
  maxDecisionValue?: number;
}

interface Props {
  alignment: AlignmentData | null;
  totalNodes: number;
  coverage: number;
  profileVersion: number;
}

const TIER_CONFIG: Record<string, { label: string; color: string; glow: string; description: string }> = {
  'guessing':      { label: 'Guessing',      color: '#64748b', glow: 'rgba(100,116,139,0.3)', description: 'Not enough data to form identity' },
  'limited':       { label: 'Limited',        color: '#f59e0b', glow: 'rgba(245,158,11,0.3)',  description: 'Some patterns recognized, low confidence' },
  'operational':   { label: 'Operational',    color: '#6366f1', glow: 'rgba(99,102,241,0.3)',   description: 'Reliable for routine tasks' },
  'high_fidelity': { label: 'High Fidelity', color: '#22c55e', glow: 'rgba(34,197,94,0.3)',    description: 'Deeply calibrated to your identity' },
};

const AUTONOMY_MATRIX: Record<string, AutonomyLimits> = {
  'guessing':      { canRespondWithoutReview: false, canExecuteWorkflows: false, canSendExternalMessages: false, canMakeFinancialDecisions: false, maxDecisionValue: 0 },
  'limited':       { canRespondWithoutReview: true,  canExecuteWorkflows: false, canSendExternalMessages: false, canMakeFinancialDecisions: false, maxDecisionValue: 0 },
  'operational':   { canRespondWithoutReview: true,  canExecuteWorkflows: true,  canSendExternalMessages: true,  canMakeFinancialDecisions: false, maxDecisionValue: 100 },
  'high_fidelity': { canRespondWithoutReview: true,  canExecuteWorkflows: true,  canSendExternalMessages: true,  canMakeFinancialDecisions: true,  maxDecisionValue: 1000 },
};

export const CoherencePanel: React.FC<Props> = ({ alignment, totalNodes, coverage, profileVersion }) => {
  const tier = alignment?.tier || 'guessing';
  const score = alignment?.score || 0;
  const config = TIER_CONFIG[tier] || TIER_CONFIG['guessing'];
  const autonomy = AUTONOMY_MATRIX[tier] || AUTONOMY_MATRIX['guessing'];
  const breakdown = alignment?.breakdown;

  // SVG arc calculations
  const radius = 80;
  const circumference = 2 * Math.PI * radius;
  const progress = (score / 100) * circumference;

  return (
    <div className="flex-1 p-6 overflow-auto">
      <div className="max-w-4xl mx-auto space-y-6">

        {/* ── Header ── */}
        <div className="flex items-center gap-3 mb-2">
          <span className="text-2xl">⚡</span>
          <div>
            <h2 className="text-lg font-bold text-slate-900 dark:text-white">Global Coherence Engine</h2>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              HR-BREV-020 · Cross-layer intelligence synthesis · Profile v{profileVersion}
            </p>
          </div>
        </div>

        {/* ── Main Grid ── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

          {/* ── Alignment Arc ── */}
          <div className="lg:col-span-1 bg-white/60 dark:bg-slate-800/60 backdrop-blur-sm border border-slate-200 dark:border-slate-700/50 rounded-2xl p-6 flex flex-col items-center">
            <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-4">
              Alignment Score
            </h3>

            <div className="relative w-48 h-48">
              <svg viewBox="0 0 200 200" className="w-full h-full -rotate-90">
                {/* Background arc */}
                <circle
                  cx="100" cy="100" r={radius}
                  fill="none"
                  stroke="currentColor"
                  className="text-slate-200 dark:text-slate-700"
                  strokeWidth="10"
                />
                {/* Progress arc */}
                <circle
                  cx="100" cy="100" r={radius}
                  fill="none"
                  stroke={config.color}
                  strokeWidth="10"
                  strokeLinecap="round"
                  strokeDasharray={circumference}
                  strokeDashoffset={circumference - progress}
                  style={{ 
                    transition: 'stroke-dashoffset 1.5s ease-out',
                    filter: `drop-shadow(0 0 8px ${config.glow})`,
                  }}
                />
              </svg>
              {/* Center text */}
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-3xl font-black text-slate-900 dark:text-white">
                  {score}%
                </span>
                <span
                  className="text-xs font-bold px-2 py-0.5 rounded-full mt-1"
                  style={{ 
                    backgroundColor: `${config.color}20`,
                    color: config.color,
                    border: `1px solid ${config.color}40`,
                  }}
                >
                  {config.label}
                </span>
              </div>
            </div>

            <p className="text-xs text-slate-500 dark:text-slate-400 text-center mt-3 max-w-[200px]">
              {config.description}
            </p>
          </div>

          {/* ── Breakdown + Stats ── */}
          <div className="lg:col-span-2 space-y-5">

            {/* Cognitive Breakdown */}
            <div className="bg-white/60 dark:bg-slate-800/60 backdrop-blur-sm border border-slate-200 dark:border-slate-700/50 rounded-2xl p-5">
              <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-4">
                Cognitive Breakdown
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {[
                  { label: 'Facts', value: breakdown?.factNodes ?? 0, icon: '📋', color: '#6366f1' },
                  { label: 'Preferences', value: breakdown?.preferenceNodes ?? 0, icon: '❤', color: '#ec4899' },
                  { label: 'Boundaries', value: breakdown?.boundaryNodes ?? 0, icon: '🛡️', color: '#ef4444' },
                  { label: 'Behaviors', value: breakdown?.behaviorNodes ?? 0, icon: '⚡', color: '#f59e0b' },
                  { label: 'Evidence', value: breakdown?.evidenceNodes ?? 0, icon: '🔬', color: '#22c55e' },
                  { label: 'Coverage', value: `${Math.round(coverage)}%`, icon: '🎯', color: '#8b5cf6' },
                ].map(item => (
                  <div
                    key={item.label}
                    className="bg-slate-50 dark:bg-slate-700/30 rounded-xl p-3 border border-slate-100 dark:border-slate-700/30"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm">{item.icon}</span>
                      <span className="text-xs text-slate-500 dark:text-slate-400">{item.label}</span>
                    </div>
                    <span className="text-lg font-bold" style={{ color: item.color }}>
                      {item.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Autonomy Matrix */}
            <div className="bg-white/60 dark:bg-slate-800/60 backdrop-blur-sm border border-slate-200 dark:border-slate-700/50 rounded-2xl p-5">
              <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-4">
                Autonomy Level — What Your Replica Can Do
              </h3>
              <div className="space-y-2">
                {[
                  { label: 'Respond without human review', value: autonomy.canRespondWithoutReview, icon: '💬' },
                  { label: 'Execute workflows autonomously', value: autonomy.canExecuteWorkflows, icon: '⚙️' },
                  { label: 'Send external messages (email/SMS)', value: autonomy.canSendExternalMessages, icon: '📤' },
                  { label: 'Make financial decisions', value: autonomy.canMakeFinancialDecisions, icon: '💰' },
                ].map(item => (
                  <div
                    key={item.label}
                    className="flex items-center justify-between px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-700/20"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-sm">{item.icon}</span>
                      <span className="text-sm text-slate-700 dark:text-slate-300">{item.label}</span>
                    </div>
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                      item.value
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 border border-green-200 dark:border-green-700/50'
                        : 'bg-slate-100 dark:bg-slate-800/50 text-slate-400 dark:text-slate-500 border border-slate-200 dark:border-slate-700/50'
                    }`}>
                      {item.value ? '✓ Enabled' : '✗ Locked'}
                    </span>
                  </div>
                ))}
                {autonomy.maxDecisionValue !== undefined && autonomy.maxDecisionValue > 0 && (
                  <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-700/20">
                    <div className="flex items-center gap-2">
                      <span className="text-sm">📊</span>
                      <span className="text-sm text-slate-700 dark:text-slate-300">Max autonomous decision value</span>
                    </div>
                    <span className="text-xs font-bold text-indigo-500">
                      ${autonomy.maxDecisionValue.toLocaleString()}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* System Stats */}
            <div className="grid grid-cols-3 gap-3">
              {[
                { label: 'Total Nodes', value: totalNodes, icon: '🧠' },
                { label: 'Profile Version', value: `v${profileVersion}`, icon: '📌' },
                { label: 'Identity Coverage', value: `${Math.round(coverage)}%`, icon: '🎯' },
              ].map(stat => (
                <div
                  key={stat.label}
                  className="bg-white/60 dark:bg-slate-800/60 backdrop-blur-sm border border-slate-200 dark:border-slate-700/50 rounded-xl p-4 text-center"
                >
                  <span className="text-xl block mb-1">{stat.icon}</span>
                  <span className="text-lg font-bold text-slate-900 dark:text-white block">{stat.value}</span>
                  <span className="text-[10px] text-slate-500 dark:text-slate-400">{stat.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoherencePanel;
